<script setup lang="ts" generic="T extends Record<string, string>">
import { computed, inject, ref } from 'vue';
import { FLOAT_PATTERN, NxCurrencyInput, NxValidatedTextInput } from './../Inputs';
import {
  formatSummaryDecimal,
  type NxTableSummaryCellSlotProps,
  type NxTableSummaryFormatContext,
  type NxTableSummaryValue,
} from './tableSummary';
import { currencyDecoration } from './../Inputs/currencyDecoration';
import { i18nInjectionKey } from './../Inputs/internal';
import {
  TRAILING_CURRENCY_CODE_PX,
  TRAILING_DECO_PX,
} from './../Inputs/composables/inputTrailingZone';
import {
  isSameCurrencyAmount,
  splitCurrencyText,
  toCurrencyCsvCell,
  toCurrencyInputModelValue,
} from './currencyCell';
import { defaultEditCellFocusRef, onEditCellKeydown, PartialOrNull } from './internal';
import { default as NxTableColumn, type NxTableColumnProps } from './NxTableColumn.vue';

const props = withDefaults(
  defineProps<
    Omit<NxTableColumnProps<T, keyof T, number | string>, 'toCsvString'> & {
      /** value that will be used for the csv export */
      toCsvString?: NxTableColumnProps<T, keyof T, number | string>['toCsvString'];
      /**
       * Default ISO 4217 code when the row has no per-row currency field
       * (e.g. `EUR`). Used for filter/create cells and as fallback.
       */
      currency?: string;
      /**
       * Optional row field that holds the ISO code for that row (e.g. denorm
       * `currencyCode` from the currency relation). When set and non-empty,
       * amount cells format with that currency's symbol (€, CHF, …).
       */
      currencyCodeKey?: keyof T & string;
      /**
       * When true, always format with the column `currency` prop and ignore a
       * per-row currencyCode. Sales-document child grids (positions / charges)
       * use this so a stale stamp from the previous document currency cannot
       * keep showing € after the header was switched to CHF.
       */
      lockCurrency?: boolean;
    }
  >(),
  {
    summary: 'sum',
    filterable: true,
    resizeable: true,
    reorderable: true,
    sortable: true,
    editable: true,
    defaultVisible: true,
    layout: 'row',
    toCsvString: undefined,
    currency: 'EUR',
    lockCurrency: false,
  }
);

const i18nInjection = inject(i18nInjectionKey)!;

/** ISO code → Intl formatter; symbol so amounts read "12,50 €" / "12,50 CHF". */
function formatterFor(code: string): Intl.NumberFormat {
  const currency = /^[A-Za-z]{3}$/.test(code) ? code.toUpperCase() : props.currency;
  return new Intl.NumberFormat(i18nInjection.locale.value, {
    style: 'currency',
    currency,
    currencyDisplay: 'symbol',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** ISO code for edit/create cells — follows the column default (document currency). */
const inputCurrency = computed(() => {
  const raw = (props.currency ?? 'EUR').trim().toUpperCase();
  return /^[A-Z]{3}$/.test(raw) ? raw : 'EUR';
});

function currencyCodeForRow(rowData: T): string {
  if (props.lockCurrency) return inputCurrency.value;
  if (props.currencyCodeKey) {
    const raw = rowData[props.currencyCodeKey];
    if (typeof raw === 'string' && raw.trim()) {
      const code = raw.trim().toUpperCase();
      if (/^[A-Z]{3}$/.test(code)) return code;
    }
  }
  return inputCurrency.value;
}

function summaryCurrencyUnit(row: T): string {
  return props.summaryUnit?.(row) ?? currencyCodeForRow(row);
}
function summaryCurrencyFormat(
  value: NxTableSummaryValue,
  context: NxTableSummaryFormatContext<T>
): string {
  if (props.summaryFormat) return props.summaryFormat(value, context);
  return formatSummaryDecimal(value, context.locale, {
    style: 'currency',
    currency: context.unit ?? inputCurrency.value,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    signDisplay:
      props.summaryShowSign &&
      value !== null &&
      !String(value).startsWith('-') &&
      Number(value) !== 0
        ? 'always'
        : 'auto',
  });
}

function currencyFormatForRow(rowData: T): Intl.NumberFormat {
  return formatterFor(currencyCodeForRow(rowData));
}

function displayValue(rowData: T): { amount: string; label: string; reservePx: number } | null {
  const raw = rowData[props.columnId];
  if (raw === null || raw === undefined || (typeof raw === 'string' && raw.trim() === '')) {
    return null;
  }
  const numericValue = Number(raw);
  if (!Number.isFinite(numericValue)) return null;

  const currency = currencyCodeForRow(rowData);
  const formatter = currencyFormatForRow(rowData);
  const decoration = currencyDecoration(currency, i18nInjection.locale.value);
  const amount = formatter
    .formatToParts(numericValue)
    .filter((part) => part.type !== 'currency')
    .map((part) => part.value)
    .join('')
    .trim();

  return { amount, label: decoration.label, reservePx: decoration.reservePx };
}

/**
 * The total laid out like a body cell — amount, then the label in its reserved
 * slot — so it stands right under the amounts (BLP/nx#3200). Only for a
 * computed total in the column's own format; a custom `summaryFormat` and the
 * other states (loading, „—", …) keep the plain text.
 */
function summaryParts(
  summary: NxTableSummaryCellSlotProps
): { amount: string; label: string; reservePx: number } | null {
  if (summary.state !== 'value' || props.summaryFormat) return null;
  const parts = splitCurrencyText(summary.text);
  if (!parts) return null;
  const reservePx =
    Array.from(parts.label).length === 1 ? TRAILING_DECO_PX + 6 : TRAILING_CURRENCY_CODE_PX;
  return { ...parts, reservePx };
}

function toCsvString_(rowData: T, rowIndex: number) {
  if (props.toCsvString) {
    return props.toCsvString(rowData, rowIndex);
  }
  return toCurrencyCsvCell(rowData[props.columnId]);
}

const filterCellModel = ref<string>('');
const createCellModel = ref<number | string>('');

function onQueryLoaded(value: (number | string)[]) {
  filterCellModel.value = value.length > 0 ? `${value[0]}` : '';
}
</script>

<template>
  <!-- @vue-generic {T, keyof T, number | string} -->
  <NxTableColumn
    v-bind="{
      ...props,
      summaryUnit: summaryCurrencyUnit,
      summaryFormat: summaryCurrencyFormat,
      filterKind: props.filterKind ?? 'currency',
      onQueryLoaded: props.onQueryLoaded ? props.onQueryLoaded : onQueryLoaded,
      toCsvString: toCsvString_,
    }"
  >
    <template #summaryCell="summaryProps">
      <slot name="summaryCell" v-bind="summaryProps">
        <template
          v-for="parts in [summaryParts(summaryProps)]"
          :key="parts?.amount ?? summaryProps.text"
        >
          <span
            v-if="parts"
            class="currency-frame"
            :style="{ '--currency-reserve': `${parts.reservePx}px` }"
          >
            <span class="currency-amount">{{ parts.amount }}</span>
            <!-- The non-breaking space keeps the total's text „580,00 €" as
                 the formatter wrote it (screen readers, copying, e2e); the
                 label is pinned to the end, so the glyph does not move. -->
            <span class="currency-label">&nbsp;{{ parts.label }}</span>
          </span>
          <span v-else class="currency-summary-text">{{ summaryProps.text }}</span>
        </template>
      </slot>
    </template>
    <template v-if="$slots.headerCell" #headerCell>
      <slot name="headerCell"></slot>
    </template>
    <template
      #filterCell="{
        setFilterValue,
        disabled,
        registerFocus,
        focusAbove,
        focusBelow,
        focusLeft,
        focusRight,
      }"
    >
      <slot
        name="filterCell"
        v-bind="{
          setFilterValue,
          disabled,
          registerFocus,
          focusAbove,
          focusBelow,
          focusLeft,
          focusRight,
        }"
      >
        <NxValidatedTextInput
          :ref="defaultEditCellFocusRef(registerFocus)"
          v-model:model-value="filterCellModel"
          height="xs"
          placeholder=""
          :disabled="disabled"
          inputmode="decimal"
          :pattern="FLOAT_PATTERN"
          @update:model-value="
            (value: string) => {
              setFilterValue(value !== '' ? [value] : []);
            }
          "
          @keydown="
            (e: KeyboardEvent) =>
              onEditCellKeydown(e, {
                focusAbove,
                focusBelow,
                focusLeft,
                focusRight,
                inputType: 'text',
              })
          "
        ></NxValidatedTextInput>
      </slot>
    </template>
    <template #cell="{ rowData, rowIndex }">
      <slot name="cell" v-bind="{ rowData, rowIndex }">
        <template v-for="formatted in [displayValue(rowData)]" :key="formatted?.amount ?? 'empty'">
          <span
            v-if="formatted"
            class="currency-frame"
            :style="{ '--currency-reserve': `${formatted.reservePx}px` }"
          >
            <span class="currency-amount">{{ formatted.amount }}</span>
            <span class="currency-label">{{ formatted.label }}</span>
          </span>
          <span v-else></span>
        </template>
      </slot>
    </template>
    <template #createCell="{ setPartialRowData }">
      <slot name="createCell" v-bind="{ setPartialRowData }">
        <NxCurrencyInput
          v-model="createCellModel"
          :currency="inputCurrency"
          :placeholder="`${props.displayName ?? (props.columnId as string)}`"
          @update:model-value="
            (value) => {
              setPartialRowData({
                [props.columnId]: `${value}`,
              } as PartialOrNull<T>);
            }
          "
        ></NxCurrencyInput>
        <!-- currency prop is reactive (document currency); chip/€ updates with it -->
      </slot>
    </template>
    <template
      #editCell="{
        rowData,
        rowIndex,
        onEdit,
        registerFocus,
        focusAbove,
        focusBelow,
        focusLeft,
        focusRight,
      }"
    >
      <slot
        name="editCell"
        v-bind="{
          rowData,
          rowIndex,
          onEdit,
          registerFocus,
          focusAbove,
          focusBelow,
          focusLeft,
          focusRight,
        }"
      >
        <div class="nx-ghost-cell">
          <NxCurrencyInput
            :ref="defaultEditCellFocusRef(registerFocus)"
            :model-value="toCurrencyInputModelValue(rowData[props.columnId])"
            :currency="currencyCodeForRow(rowData)"
            height="xs"
            :model-modifiers="{ lazy: true }"
            @update:model-value="
              (value) => {
                if (isSameCurrencyAmount(value, rowData[props.columnId] as string)) return;
                onEdit({
                  [props.columnId]: `${value}`,
                } as Partial<T>);
              }
            "
            @keydown="
              (e: KeyboardEvent) =>
                onEditCellKeydown(e, {
                  focusAbove,
                  focusBelow,
                  focusLeft,
                  focusRight,
                  inputType: 'text',
                })
            "
          ></NxCurrencyInput>
        </div>
      </slot>
    </template>
  </NxTableColumn>
</template>

<style scoped>
.currency-frame {
  position: relative;
  display: block;
  inline-size: 100%;
  min-inline-size: 0;
  white-space: nowrap;
}

.currency-amount {
  display: block;
  overflow: hidden;
  padding-inline-end: calc(var(--currency-reserve) + 0.125em);
  text-align: end;
  text-overflow: ellipsis;
}

.currency-summary-text {
  display: block;
  min-inline-size: 0;
  overflow: hidden;
  text-align: end;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.currency-label {
  position: absolute;
  inset-block-start: 0;
  inset-inline-end: 0;
  padding-inline-start: 0.45em;
  font: inherit;
  letter-spacing: normal;
}
</style>
